import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetBankAccountsQuery,
  useDeleteBankAccountMutation,
} from '../../../store/api/financeApi';

function BankAccountsList() {
  const navigate = useNavigate();
  const { data: accounts = [], isLoading } = useGetBankAccountsQuery();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteBankAccount(deleteId).unwrap();
      setDeleteId(null);
    } catch (err) {
      alert(err?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Bank Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/finance/bank-accounts/new')}
        >
          Add Bank Account
        </Button>
      </Box>

      <Card>
        <CardContent>
          {isLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : accounts.length === 0 ? (
            <Typography color="text.secondary">No bank accounts. Add one to get started.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Account Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bank Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Opening Balance</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.accountNumber || '–'}</TableCell>
                      <TableCell>{row.bankName || '–'}</TableCell>
                      <TableCell>{row.branch || '–'}</TableCell>
                      <TableCell>{Number(row.openingBalance || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
                      <TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => row.id && navigate(`/finance/bank-accounts/${row.id}`)}
                          disabled={!row.id}
                          title={row.id ? 'Edit' : ''}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete bank account?</DialogTitle>
        <DialogContent>This cannot be undone. Make sure no cheques are linked.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BankAccountsList;
